import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersService } from '../services/api';
import { formatPersonName } from '../utils/personName';
import { formatDni } from '../utils/dni';
import ConfirmPopup from '../components/ConfirmPopup';
import './Dashboard.css';
import './Usuarios.css';
import './PlantillasMail.css';

const ListaNegra = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reactivatingId, setReactivatingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await usersService.getInactiveUsers();
      setUsers(data);
    } catch (err) {
      setError('Error al cargar la lista de usuarios inactivos.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleReactivar = async (user) => {
    if (reactivatingId) return;
    setReactivatingId(user.id);
    setMessage({ type: '', text: '' });
    try {
      await usersService.updateUser(user.id, { is_active: true });
      setMessage({ type: 'success', text: `${formatPersonName(user.first_name) || user.email} ha sido reactivado.` });
      loadUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'No se pudo reactivar el usuario.' });
    } finally {
      setReactivatingId(null);
    }
  };

  return (
    <>
      <div className="dashboard-header plantillas-mail-header">
        <button
          type="button"
          className="plantillas-mail-back"
          onClick={() => navigate('/dashboard/configuracion')}
          aria-label="Volver a Configuración"
        >
          ← Volver a Configuración
        </button>
        <h1>Lista Negra</h1>
        <p>Usuarios marcados como inactivos. No pueden iniciar sesión. Puedes reactivarlos desde aquí.</p>
      </div>

      {message.text && message.type !== 'error' && (
        <div className="dashboard-inscripcion-msg dashboard-inscripcion-ok">
          {message.text}
        </div>
      )}

      <ConfirmPopup
        open={Boolean(error) || (Boolean(message.text) && message.type === 'error')}
        title="Error"
        message={error || message.text}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => { setError(''); setMessage({ text: '', type: '' }); }}
        onCancel={() => { setError(''); setMessage({ text: '', type: '' }); }}
        variant="danger"
      />

      <section className="dashboard-section">
        {loading ? (
          <div className="usuarios-loading">
            <div className="loading-spinner" />
            <p>Cargando usuarios inactivos...</p>
          </div>
        ) : (
          <div className="usuarios-table-container">
            <table className="usuarios-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Nombre</th>
                  <th>Apellido</th>
                  <th>DNI</th>
                  <th>Roles</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="usuarios-empty">
                      No hay usuarios inactivos
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.email || '-'}</td>
                      <td>{formatPersonName(user.first_name) || '-'}</td>
                      <td>{formatPersonName(user.last_name) || '-'}</td>
                      <td>{user.dni ? formatDni(user.dni) : '-'}</td>
                      <td>
                        {user.groups && user.groups.length > 0 ? (
                          <div className="usuarios-groups">
                            {user.groups.map((group, index) => (
                              <span key={index} className="usuarios-group-badge">
                                {group}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="usuarios-group-badge">Estudiantes</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="usuarios-btn usuarios-btn-primary"
                          disabled={reactivatingId === user.id}
                          onClick={() => handleReactivar(user)}
                          title="Reactivar usuario"
                        >
                          {reactivatingId === user.id ? '...' : 'Reactivar'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};

export default ListaNegra;
